import React, { useEffect, useState, useMemo } from 'react';
import { Dropdown, Space, Card, Flex } from 'antd';
import type { MenuProps } from 'antd';
import {
  WarningIcon,
  TruckIcon,
  ClockIcon,
  LocationIcon,
  ChevronDownIcon,
  AiStar,
  LowInventoryIcon,
  TemperatureGxpReportIcon,
  type IconProps,
} from '../../../../assets/icons/icons';
import { buildNotificationTriggerType } from '@/common/utils/notificationUtils';
import { CustomShipmentText } from '@/components';
import { TriggerType } from '@/common/static_data';
import { useSelector } from 'react-redux';
import { getPermissions } from '@/common/helper/getPermissions';
import type { RootState as AppRootState } from '@/redux-toolkit/store';

interface SelectedNotification {
  alert_type?: string;
  [key: string]: unknown;
}

interface TriggerTypeCardProps {
  setAlertType: (alertTypeId: string) => void;
  notificationsTriggerType: TriggerType[];
  selected?: SelectedNotification | null;
  isSidePannel?: boolean;
  isInventory?: boolean;
}

type IconMapKey =
  | 'WarningIcon'
  | 'TruckIcon'
  | 'ClockIcon'
  | 'LocationIcon'
  | 'AiStar'
  | 'LowInventoryIcon'
  | 'TemperatureGxpReportIcon';

/**
 * Component that displays a dropdown card for selecting notification trigger types
 *
 * @param setAlertType - Callback function to set the selected alert type ID
 * @param notificationsTriggerType - Array of available trigger type options
 * @param selected - Currently selected notification object (optional)
 * @param isSidePannel - Whether component is rendered in a side panel (optional)
 */
const TriggerTypeCard: React.FC<TriggerTypeCardProps> = ({
  setAlertType,
  notificationsTriggerType,
  selected,
  isSidePannel = false,
  isInventory = false,
}) => {
  const main_menu = useSelector((state: AppRootState) => state.menu.items);
  const GxP_permissions = getPermissions(main_menu, 'Settings', 'GxP reports');

  const filteredNotificationsTriggerType = useMemo(
    () =>
      GxP_permissions?.is_view
        ? notificationsTriggerType
        : notificationsTriggerType.filter(
            (item) => item.id !== 'TEMPERATURE_GXP'
          ),
    [GxP_permissions?.is_view, notificationsTriggerType]
  );

  const [items, setItems] = useState<MenuProps['items']>(
    buildNotificationTriggerType(filteredNotificationsTriggerType)
  );
  const [selectedTriggerType, setSelectedTriggerType] = useState<
    TriggerType | undefined
  >(
    isInventory
      ? (filteredNotificationsTriggerType.find(
          (item) => item.id === 'LOW_INVENTORY'
        ) ?? filteredNotificationsTriggerType[0])
      : filteredNotificationsTriggerType[0]
  );

  const iconMap: Record<
    IconMapKey,
    React.ComponentType<Pick<IconProps, 'fill' | 'color' | 'width' | 'height'>>
  > = {
    WarningIcon,
    TruckIcon,
    ClockIcon,
    LocationIcon,
    AiStar,
    LowInventoryIcon,
    TemperatureGxpReportIcon,
  };

  useEffect(() => {
    if (selectedTriggerType) {
      const arr = (
        buildNotificationTriggerType(filteredNotificationsTriggerType) ?? []
      ).filter(
        (x) =>
          x?.key !== selectedTriggerType.key &&
          x?.key !== `${selectedTriggerType.key}-divider`
      );
      setItems(arr);
    }
  }, [selectedTriggerType, filteredNotificationsTriggerType]);

  useEffect(() => {
    if (selected?.alert_type) {
      const selectedItem = filteredNotificationsTriggerType.find(
        (x) => x.id === selected.alert_type
      );
      if (selectedItem) {
        setSelectedTriggerType(selectedItem);
      }
    }
  }, [selected?.alert_type, filteredNotificationsTriggerType]);

  const handleTriggerTypeClick = (info: { key: string }) => {
    const selectedItem = filteredNotificationsTriggerType.find(
      (x) => x.key === info.key
    );
    if (selectedItem) {
      setSelectedTriggerType(selectedItem);
      setAlertType(selectedItem.id);
    }
  };
  return (
    <>
      <div className='trigger-type-wrapper'>
        <CustomShipmentText
          text={'Trigger type'}
          fontSize='14px'
          fontWeight={600}
          marginBottom='9px'
        ></CustomShipmentText>
        <Dropdown
          menu={{
            items,
            onClick: handleTriggerTypeClick,
            className: `p-0 trigger-dropdown ${isSidePannel ? 'sidepannel' : ''}`,
          }}
          trigger={['click']}
          open={selected || isInventory ? false : undefined}
        >
          <Card className='trigger-type-card'>
            <Flex className='trigger-type-card-content' gap={16}>
              {(() => {
                const iconKey = selectedTriggerType?.icon;
                const Icon =
                  iconKey && iconKey in iconMap
                    ? iconMap[iconKey as IconMapKey]
                    : null;
                return Icon ? (
                  <Icon
                    fill='var(--text-primary)'
                    color='var(--text-primary)'
                    width={24}
                    height={24}
                  />
                ) : null;
              })()}
              <div className='type-content-wrapper'>
                <h6 className='content-text'>{selectedTriggerType?.title}</h6>
                <p className='content-text font-12 fw-400'>
                  {selectedTriggerType?.text}
                </p>
              </div>
              {!selected && !isInventory && (
                <div className='trigger-type-chevron'>
                  <Space>
                    <ChevronDownIcon />
                  </Space>
                </div>
              )}
            </Flex>
          </Card>
        </Dropdown>
      </div>
    </>
  );
};
export default TriggerTypeCard;
